import { zipSync } from 'fflate';
import { expressApi } from '@/state/api/expressApi';
import { triggerBrowserDownload } from '@/shared/utils/triggerBrowserDownload';
import type {
  PageDto,
  TranslatedFileSummaryDto,
  TranslationJobSummaryDto,
  TranslationJobResponseDto,
  RetryTranslationJobRequestDto,
  RetryTranslationJobResponseDto,
} from '@/types';

/**
 * Blob on success, parsed JSON on failure. A blanket `.blob()` also wrapped error bodies, so an RFC 7807 problem
 * arrived as a Blob and the XTM Cloud guard in the base query could not see it — a rejected XTM Cloud password
 * during a download still ended on "access denied".
 */
const blobOrProblem = async (response: Response) =>
  response.ok ? response.blob() : response.json().catch(() => undefined);

/** Like `blobOrProblem`, but the bytes, as they go straight into an archive. */
const bytesOrProblem = async (response: Response) =>
  response.ok ? response.arrayBuffer() : response.json().catch(() => undefined);

export interface ListTranslationJobsArgs {
  uploadBatchId: string;
  size?: number;
}

export interface ListTranslationJobsResult {
  data: PageDto<TranslationJobSummaryDto>;
  retryAfter: number | null;
}

export const translateApi = expressApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /translation-jobs?uploadBatchId={id}&size=100 — list translation jobs for a batch.
     * Handles Retry-After header for polling while jobs are still processing.
     */
    listTranslationJobs: builder.query<ListTranslationJobsResult, ListTranslationJobsArgs>({
      query: ({ uploadBatchId, size = 100 }) => ({
        url: '/translation-jobs',
        params: { uploadBatchId, size },
      }),
      transformResponse: (response: PageDto<TranslationJobSummaryDto>, meta) => {
        const retryAfterHeader = meta?.response?.headers.get('Retry-After');
        const retryAfter = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : null;
        return { data: response, retryAfter };
      },
      providesTags: ['TranslationJobs'],
    }),

    /**
     * GET /translation-jobs/{jobId} — get full details for a single translation job.
     * Includes translatedFiles array when completed.
     */
    getTranslationJob: builder.query<TranslationJobResponseDto, string>({
      query: (jobId) => `/translation-jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'TranslationJobs', id: jobId }],
    }),

    /**
     * POST /translation-jobs/{jobId}/retry — retry a failed translation job.
     * Optionally allows specifying a subset of targetLangs to retry.
     */
    retryTranslationJob: builder.mutation<
      RetryTranslationJobResponseDto,
      { jobId: string; body?: RetryTranslationJobRequestDto }
    >({
      query: ({ jobId, body }) => ({
        url: `/translation-jobs/${jobId}/retry`,
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: ['TranslationJobs'],
    }),

    /**
     * GET /translated-files/{translatedFileId} — download a translated file as blob.
     * Triggers a browser download via a temporary <a> element.
     */
    downloadTranslatedFile: builder.mutation<void, { translatedFileId: string; filename: string }>({
      queryFn: async ({ translatedFileId, filename }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/translated-files/${translatedFileId}`,
          responseHandler: blobOrProblem,
        });

        if (result.error) {
          return { error: result.error };
        }

        triggerBrowserDownload(result.data as Blob, filename);
        return { data: undefined };
      },
    }),

    /**
     * Every file of one document, one per target language, as a single ZIP — so "Download all" is one download
     * rather than one per language, which the browser may block or ask about and which lands loose files. The API
     * has an archive for a whole batch only, so this one is built here, from GET /translated-files/{id} per file;
     * that read is also what records each file as downloaded. Each file sits in a folder named after its language,
     * as in the batch ZIP, so files that share a name stay apart. Stored rather than deflated: office and PDF files
     * are compressed already, and the archive is assembled on the page's own thread.
     *
     * One failed read fails the whole archive: a ZIP short of a language would pass for the complete set.
     */
    downloadTranslatedDocument: builder.mutation<
      void,
      { files: Pick<TranslatedFileSummaryDto, 'id' | 'targetLang' | 'filename'>[]; archiveName: string }
    >({
      queryFn: async ({ files, archiveName }, _api, _extraOptions, baseQuery) => {
        const readFile = async (file: { id: string }) =>
          baseQuery({ url: `/translated-files/${file.id}`, responseHandler: bytesOrProblem });
        const results = await Promise.all(files.map(readFile));

        const failed = results.find((result) => result.error);
        if (failed?.error) {
          return { error: failed.error };
        }

        const entries: Record<string, Uint8Array> = {};
        files.forEach((file, index) => {
          entries[`${file.targetLang}/${file.filename}`] = new Uint8Array(results[index].data as ArrayBuffer);
        });

        triggerBrowserDownload(
          new Blob([zipSync(entries, { level: 0 })], { type: 'application/zip' }),
          archiveName,
        );
        return { data: undefined };
      },
    }),
  }),
});

export const {
  useListTranslationJobsQuery,
  useLazyListTranslationJobsQuery,
  useGetTranslationJobQuery,
  useLazyGetTranslationJobQuery,
  useRetryTranslationJobMutation,
  useDownloadTranslatedFileMutation,
  useDownloadTranslatedDocumentMutation,
} = translateApi;
